import React, { useState, useEffect } from 'react'
//basic components
import { Button } from '../../../components/ui/buttons/Button'
import Label from '../../../components/ui/labels/Label'
import Select from 'react-select'
import { SelectContainer } from '../../../components/ui/layout/SelectContainer'
//group control components
import { GroupDetailsForm } from './groupComponents/GroupDetailsForm'
import { GroupPermissions } from './groupComponents/GroupPermissions'
//hooks
import { useLoading } from '../../../contexts/LoadingContext';
import { useError } from '../../../contexts/ErrorContext'
//api
import { getPermissionGroupNamesAPI } from "../../../services/groupsAPI"
import { getGroupsPermissionsAPI } from "../../../services/groupsAPI"

/**
 * Add and update groups, update group permissions
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Admin Dashboard
 */
export const GroupControl = () => {

    const [showGroupForm, setShowGroupForm] = useState(false)
    
    const [groupNameOptions, setGroupNameOptions] = useState([]) //formatted group id and name for select component
    const [selectedGroup, setSelectedGroup] = useState(null) //group selected in select component
    const [groupDetails, setGroupDetails] = useState(null) 
    const [groupPermissions, setGroupPermissions] = useState(null) 

    const [isAddNewGroup, setIsAddNewGroup] = useState(false)

    const { showLoading, hideLoading } = useLoading();

    const {showError} = useError()

    //get all groups and format names into options for select component
    const fetchGroups = async () => {
        showLoading("Getting groups...")
        const response = await getPermissionGroupNamesAPI();

        if (!response.success) {
            hideLoading()

            showError(`Error getting groups: ${response.message}`)

            return
        }
        
        const formattedGroups = response.data.map(group => ({
            value: group.GroupID,
            label: group.Group_Name
        }));

        //set options for group names and get all the group data in state
        setGroupNameOptions(formattedGroups)
        
        hideLoading()
    }

    //get permissions for selected group
    const fetchPermissions = async(GroupID) => {
        showLoading("Getting group permissions...")
        const response = await getGroupsPermissionsAPI(GroupID);

        if (!response.success) {
            hideLoading()

            showError(`Error getting permissions: ${response.message}`)

            return
        }
        
        setGroupPermissions(response.data.permissions)
        setGroupDetails(response.data.groupDetails)
        setShowGroupForm(true)

        hideLoading()
    }

    //fetch groups when component loads
    useEffect(() => {
        fetchGroups()
    }, [])

    //fetch permissions when selected group changes
    useEffect(() => {
        if(selectedGroup){
            fetchPermissions(selectedGroup.value)
        }
    }, [selectedGroup])


    return (
        <>
            {!isAddNewGroup ? (
                <>
                    {/* add new group button */}
                    <div className='flex justify-end'>
                        <Button 
                            variant='success' 
                            onClick={() =>  {
                                setSelectedGroup(null)
                                setGroupDetails(null)
                                setGroupPermissions(null)
                                setShowGroupForm(true)
                                setIsAddNewGroup(true)
                                }} 
                            className='mb-4'>
                                <i className="fa-solid fa-circle-plus mr-2"></i>Add New Group
                        </Button>
                    </div>

                    {/* select a group */}
                    <Label htmlFor="groupsDropdown">Select a group</Label>
                    <SelectContainer>
                        <Select
                            value={selectedGroup}
                            options={groupNameOptions}
                            name="groupsDropdown"
                            onChange={(x) => setSelectedGroup(x)}
                            menuPosition='fixed'
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </SelectContainer>
                </>
            ) : null}
            

            {/* form to view or add group */}
            {showGroupForm && (isAddNewGroup || selectedGroup)
                ? 
                <>
                <GroupDetailsForm 
                    groupDetails={groupDetails} 
                    setSelectedGroup={setSelectedGroup} 
                    setGroupNameOptions={setGroupNameOptions}
                    isAddNewGroup={isAddNewGroup}
                    setIsAddNewGroup={setIsAddNewGroup}
                /> 
                

                </>
                : null 
            }

            {/* can only display permissions if group is selected */}
            {showGroupForm && selectedGroup 
                ?
                (
                    <>
                        <hr className='mb-10 mt-2'/>

                        <GroupPermissions 
                            groupID={selectedGroup.value}
                            groupPermissions={groupPermissions}
                        />
                    </>
                )
                : null   
            }

        
        </>
    )
}